<script setup>
import { ref, onMounted } from 'vue'
import DateTimePickerCustom from '../../components/DateTimePickerCustom.vue'
import axios from 'axios'
import { useMenuStore } from '../../stores/menuStore' 
import { useUserStore } from '../../stores/userStore'
import { useCurrentUserPermissions } from '../../composables/usePermissions'
import { useRoute } from 'vue-router'
import { useProjectionsStore } from '../../stores/projections'

const { hasAnyPermission } = useCurrentUserPermissions()

const menuStore = useMenuStore()
const userStore = useUserStore()
const projectionsStore = useProjectionsStore()
const route = useRoute()

const props = defineProps({
  currentLeagueName: {
    type: String,
    default: ''
  }
})

// Define emits for parent communication
const emit = defineEmits(['outrightCreated'])

// Form state
const isSubmitting = ref(false)
const error = ref(null)

const formData = ref({
  datetime: '',
  name: ''
})

const handleSubmit = async () => {
  isSubmitting.value = true
  error.value = null

  try {
    // Split datetime into date and time
    const [date, time] = formData.value.datetime.split(', ')

    const payload = {
      game_date: formData.value.datetime,
   
      name: formData.value.name,
      league_id: menuStore.thirdMenuContent?.id
    }

    const { data } = await axios.post('/api/outrights/generate', payload)
    outrights.value.unshift(data.outright)
    // Handle successful response
    console.log('Outright generated:', data)
    
    // Emit the new outright to parent component
    if (data && data.outright) {
      emit('outrightCreated', data.outright)
    }
    
    // Reset form
    formData.value = {
      datetime: '',
      name: ''
    }
  } catch (err) {
    error.value = err.response?.data?.message || 'An error occurred while generating the outright'
    console.error('Error generating outright:', err)
  } finally {
    isSubmitting.value = false
  }
}

// Sample outrights data - replace with actual API data
const outrights = ref([

])


onMounted(() => {
  getOutrights()
})

const getOutrights = async () => {
  const leagueId = route.params.id
  console.log('leagueId', leagueId)
  const { data } = await axios.post(`/api/outrights/get-outrights`, {
    league_id: leagueId
  })
  outrights.value = data
}
</script>

<template>
  <div>
    <!-- Create New Outright Form -->
    <div class="bg-white rounded-lg shadow mb-6">
      <div v-if="hasAnyPermission(['outrights'])" class="p-6">
        <h3 class="text-lg font-semibold mb-4">Create New Outright</h3>
        
        <div class="space-y-6">
          <div>
            <label class="block text-sm text-gray-500 mb-1">Date & Time</label>
            <DateTimePickerCustom
              v-model="formData.datetime"
              placeholder="Select date and time"
            />
          </div>
          
          <div>
            <label class="block text-sm text-gray-500 mb-1">Name</label>
            <input 
              v-model="formData.name"
              type="text" 
              placeholder="e.g., NBA Championship Winner"
              class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-pink-500"
            >
          </div>

          <!-- Error message -->
          <div v-if="error" class="text-red-500 text-sm mt-2">
            {{ error }}
          </div>

          <!-- Submit Button -->
          <div
          v-if="hasAnyPermission(['outrights'])"
          class="flex justify-end mt-6">
            <button 
              @click="handleSubmit"
              :disabled="isSubmitting"
              class="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              <span v-if="isSubmitting" class="mr-2">
                <svg class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
              {{ isSubmitting ? 'Generating...' : 'Generate Outright' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Existing Outrights List -->
    <div class="bg-white rounded-lg shadow">
      <div class="p-4 border-b">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <i class="fas fa-trophy text-pink-500"></i>
            <span class="font-semibold">Existing Outrights</span>
            <span class="text-sm text-gray-500">({{ outrights.length }} outrights)</span>
          </div>
          
          <div class="flex gap-3">
            <button class="text-gray-500 hover:text-gray-700">
              <i class="fas fa-sliders-h mr-2"></i> Filters
            </button>
            <button class="text-gray-500 hover:text-gray-700">
              <i class="fas fa-download mr-2"></i> Export
            </button>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div v-if="outrights.length > 0">
        <!-- Column Headers -->
        <div :class="hasAnyPermission(['outrights']) ? 'grid grid-cols-[1fr_1fr_150px_100px_120px]' : 'grid grid-cols-[1fr_1fr_150px_100px]'" class="px-6 py-3 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase tracking-wider">
          <div>OUTRIGHT ID</div>
          <div>NAME</div>
          <div>DATE & TIME</div>
          <div>STATUS</div>
          <div v-if="hasAnyPermission(['outrights'])" class="text-right">ACTIONS</div>
        </div>

                <!-- Outright Rows -->
        <div class="divide-y divide-gray-100">
          <div 
            v-for="outright in outrights" 
            :key="outright.id" 
            :class="hasAnyPermission(['outrights']) ? 'grid grid-cols-[1fr_1fr_150px_100px_120px]' : 'grid grid-cols-[1fr_1fr_150px_100px]'"
            class="px-6 py-3 hover:bg-gray-50 items-center"
          >
            <div>
              <div class="flex items-center gap-4">
                <a 
                  :href="`/outrights/${outright.id}`" 
                  class="text-pink-500 cursor-pointer hover:text-pink-600"
                >
                  #{{ outright.id }}
                </a>
                <a 
                  :href="`/outrights/${outright.id}`" 
                  target="_blank"
                  class="text-gray-400 hover:text-gray-600 flex items-center gap-1 h-full"
                >
                  <span class="text-[14px] text-gray-400">New Tab</span>
                  <i class="fas fa-external-link-alt text-[8px] h-full"></i>
                </a>
              </div>
            </div>

            <div>
              <div class="flex items-center gap-2">
                <span 
                  class="text-gray-900 font-medium"
                >
                  {{ outright.name }}
                </span>
              </div>
            </div>

            <div class="text-sm text-gray-500">
              {{ outright.date }} {{ outright.time }}
            </div>

      

            <div>
              <span 
                class="px-2 py-1 rounded-full text-sm capitalize"
                :class="{
                  'bg-green-100 text-green-700': outright.status === 'active',
                  'bg-yellow-100 text-yellow-700': outright.status === 'draft',
                  'bg-gray-100 text-gray-700': outright.status === 'closed'
                }"
              >
                {{ outright.status }}
              </span>
            </div>

            <div
            
            v-if="hasAnyPermission(['outrights'])"
            class="text-right">
              <button class="text-gray-400 hover:text-gray-600" title="View details">
                <i class="fas fa-eye"></i>
              </button>
              <button class="text-gray-400 hover:text-gray-600" title="Edit outright">
                <i class="fas fa-edit"></i>
              </button>
              <button class="text-gray-400 hover:text-red-600" title="Delete outright">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-12">
        <i class="fas fa-trophy text-4xl text-gray-300 mb-4"></i>
        <h3 class="text-lg font-medium text-gray-500">No Outrights Yet</h3>
        <p class="text-gray-400">Create your first outright market for {{ currentLeagueName }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Outright-specific styles */
</style> 